import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { ButtonHTMLAttributes, forwardRef } from 'react';
import { cn } from './cn';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-[var(--nova-radius)] text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nova-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'bg-nova-primary text-nova-primary-foreground hover:bg-nova-primary/90',
        secondary:
          'bg-nova-secondary text-nova-secondary-foreground hover:bg-nova-secondary/80',
        outline:
          'border border-nova-border bg-nova-background hover:bg-nova-accent hover:text-nova-accent-foreground',
        ghost: 'hover:bg-nova-accent hover:text-nova-accent-foreground',
        destructive:
          'bg-nova-destructive text-nova-destructive-foreground hover:bg-nova-destructive/90',
      },
      size: {
        default: 'h-8 px-2.5',
        sm: 'h-7 px-2 text-xs',
        lg: 'h-9 px-3',
        icon: 'h-8 w-8',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
